/**
 * useQueueProcess
 * @description Library sederhana untuk mengelola antrian (queue) task berbasis Promise secara berurutan.
 * Setiap task akan dieksekusi satu per satu, dan task berikutnya akan berjalan setelah task sebelumnya selesai.
 *
 * @returns {Object} queueAPI
 * @returns {Function} queueAPI.addToQueue - Menambahkan task ke dalam queue. Task akan dieksekusi secara berurutan.
 *   @param {Object} params
 *   @param {Function} params.task - Fungsi yang mengembalikan Promise, akan dijalankan sebagai task.
 *   @param {Function} params.onComplete - Fungsi callback yang dipanggil setelah task selesai (resolve/reject).
 * @returns {Function} queueAPI.taskIsCompleted - Menandai bahwa task aktif telah selesai secara manual dan melanjutkan ke task berikutnya. Biasanya tidak perlu dipanggil manual kecuali task tidak berbasis Promise.
 *
 * @example
 * const queue = useQueueProcess()
 * // Menambahkan task ke dalam queue
 * queue.addToQueue({
 *   task: () => new Promise(resolve => {
 *     console.log('Task 1')
 *     setTimeout(resolve, 1000)
 *   }),
 *   onComplete: () => {
 *     console.log('Task 1 selesai')
 *   },
 * })
 * // Menandai bahwa task telah selesai (opsional, jika task tidak mengembalikan Promise)
 * queue.taskIsCompleted()
 */
export default function useQueueProcess() {
  const queue = []
  let isCurrentProcessCompleted = true
  let activeProcess = null

  /**
   * @description Internal function untuk menjalankan setiap task yang telah ditambahkan ke dalam queue secara berurutan.
   * @returns {void}
   */
  const processQueue = () => {
    if (!isCurrentProcessCompleted) return

    if (queue.length > 0) {
      isCurrentProcessCompleted = false
      const process = queue.shift()
      activeProcess = process
      process.task().then(() => {
        isCurrentProcessCompleted = true
        process.onComplete()
        processQueue()
      }).catch(() => {
        isCurrentProcessCompleted = true
        process.onComplete()
        processQueue()
      })
    }
  }

  /**
   * @description Function untuk menambahkan task ke dalam queue.
   * @param {Object} params - Parameter untuk menambahkan task ke dalam queue.
   * @param {Function} params.task - Fungsi yang mengembalikan Promise, akan dijalankan sebagai task.
   * @param {Function} params.onComplete - Fungsi callback yang dipanggil setelah task selesai (resolve/reject).
   * @returns {void}
   */
  const addToQueue = ({
    task,
    onComplete,
  }) => {
    queue.push({ task, onComplete })
    processQueue()
  }

  /**
   * @description Function untuk menandai bahwa task aktif telah selesai secara manual dan melanjutkan ke task berikutnya.
   * @returns {void}
   */
  const taskIsCompleted = () => {
    const { onComplete } = activeProcess
    isCurrentProcessCompleted = true
    processQueue()
    onComplete()
  }

  return {
    addToQueue,
    taskIsCompleted,
  }
}
